import { useState, type ReactNode } from "react";
import {
  Button,
  NumberInput,
  Stack,
  Text,
  VisuallyHidden,
} from "@mantine/core";
import { FormattedMessage, useIntl } from "react-intl";
import dayjs from "dayjs";
import { BottomSheet } from "../BottomSheet";
import { REPLAY_BLOCK_CLASS } from "../../../utils/statsig";

// Anything above this is a typo rather than a monthly income, so the save is blocked.
const MAX_MONTHLY_SALARY = 1_000_000;

const parseSalary = (value: number | string): number | null => {
  const salary = typeof value === "number" ? value : Number(value);
  if (!Number.isInteger(salary) || salary <= 0 || salary > MAX_MONTHLY_SALARY) {
    return null;
  }
  return salary;
};

// addIncome.dismiss is the only "Stäng" in Lokalise; every sheet reuses it rather
// than shipping a second copy of the same word.
const CloseButton = ({ onClose }: { onClose: () => void }) => (
  <Button fullWidth variant="default" onClick={onClose}>
    <FormattedMessage
      id="app.poaOnb.addIncome.dismiss"
      defaultMessage="Stäng"
    />
  </Button>
);

export function IncomeSheet({
  opened,
  onClose,
  initialSalary,
  onSave,
  isSaving,
  isError,
}: {
  opened: boolean;
  onClose: () => void;
  initialSalary?: number | null;
  onSave: (salary: number) => void;
  isSaving: boolean;
  isError: boolean;
}) {
  const intl = useIntl();
  const [value, setValue] = useState<number | string>(initialSalary ?? "");

  const salary = parseSalary(value);
  const showError = value !== "" && salary === null;

  return (
    <BottomSheet
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: "app.poaOnb.addIncome.title",
        defaultMessage: "Lägg till din inkomst",
      })}
    >
      <Stack gap="md">
        <Text size="md" lh={1.6}>
          <FormattedMessage
            id="app.poaOnb.addIncome.drawer.description"
            defaultMessage="För att ge dig en så träffsäker pensionsprognos som möjligt behöver vi veta din månadsinkomst."
          />
        </Text>

        <NumberInput
          label={intl.formatMessage({
            id: "app.poaOnb.addIncome.inputLabel",
            defaultMessage: "Inkomst före skatt (kr/mån)",
          })}
          placeholder={intl.formatMessage({
            id: "app.poaOnb.addIncome.inputPlaceholder",
            defaultMessage: "t.ex 25 000",
          })}
          size="md"
          hideControls
          inputMode="numeric"
          value={value}
          onChange={setValue}
          thousandSeparator=" "
          allowNegative={false}
          allowDecimal={false}
          min={0}
          error={
            showError
              ? intl.formatMessage({
                  id: "app.poaOverview.incomeSheet.invalid",
                  defaultMessage: "Ange en månadsinkomst i hela kronor",
                })
              : undefined
          }
          // Block the value, not the label.
          classNames={{ input: REPLAY_BLOCK_CLASS }}
        />

        {isError && (
          <Text size="sm" lh={1.6} c="red" role="alert">
            <FormattedMessage
              id="app.poaOverview.incomeSheet.saveFailed"
              defaultMessage="Inkomsten kunde inte sparas just nu. Försök igen om en stund."
            />
          </Text>
        )}

        <Button
          fullWidth
          mt={8}
          loading={isSaving}
          disabled={salary === null || isSaving}
          onClick={() => salary !== null && onSave(salary)}
        >
          <FormattedMessage
            id="app.poaOnb.addIncome.CTA"
            defaultMessage="Spara inkomst"
          />
        </Button>
        <CloseButton onClose={onClose} />
      </Stack>
    </BottomSheet>
  );
}

export function ForecastBasisSheet({
  opened,
  onClose,
  onAddIncome,
}: {
  opened: boolean;
  onClose: () => void;
  /** Only while the income is missing (PEN-358); once added, Stäng is the only action. */
  onAddIncome?: () => void;
}) {
  const intl = useIntl();

  return (
    <BottomSheet
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: "app.poaOverview.foreCast.headline",
        defaultMessage: "Så har vi räknat prognosen",
      })}
    >
      <Stack gap="md">
        <Text size="sm" lh={1.6}>
          <FormattedMessage
            id="app.poaOverview.foreCast.description"
            defaultMessage="Vi kunde inte se exakt hur mycket som betalas in till din tjänstepension just nu, så vi har räknat med ett schablonvärde istället."
          />
        </Text>
        <Text size="sm" lh={1.6}>
          <FormattedMessage
            id="app.poaOverview.foreCast.description.formula"
            defaultMessage="Beräkningen utgår från 4,5 % av din lön upp till 7,5 inkomstbasbelopp och 30 % på lön över det. Det är en uppskattning och kan skilja sig från din faktiska pension."
          />
        </Text>
        <Text size="sm" lh={1.6}>
          <FormattedMessage
            id="app.poaOverview.foreCast.fees"
            defaultMessage="Även de förväntade avgifterna är en uppskattning. Vi har räknat med en avgift på 1 % per år."
          />
        </Text>

        <Stack gap="md" mt={8}>
          {onAddIncome && (
            // Key shared with the figure link until the frame's own key for this button is confirmed
            <Button fullWidth onClick={onAddIncome}>
              <FormattedMessage
                id="app.poaOverview.foreCast.missingIncome.CTA"
                defaultMessage="Lägg till inkomst"
              />
            </Button>
          )}
          <CloseButton onClose={onClose} />
        </Stack>
      </Stack>
    </BottomSheet>
  );
}

const Fact = ({
  header,
  value,
  block,
}: {
  header: ReactNode;
  value: ReactNode;
  block?: boolean;
}) => (
  <Stack gap={2}>
    <Text size="sm" fw={300} c="var(--wallet-color-neutrals-gray-800)">
      {header}
    </Text>
    <Text
      size="sm"
      fw={550}
      lh="24.8px"
      c="var(--wallet-color-neutrals-black)"
      className={block ? REPLAY_BLOCK_CLASS : undefined}
    >
      {value}
    </Text>
  </Stack>
);

export function PoaDetailsSheet({
  opened,
  onClose,
  validTo,
  giverName,
  giverPersonalNumber,
}: {
  opened: boolean;
  onClose: () => void;
  validTo: string | null;
  giverName: string | null;
  giverPersonalNumber: string | null;
}) {
  const intl = useIntl();
  const giver = [giverPersonalNumber, giverName].filter(Boolean).join(", ");

  return (
    <BottomSheet
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: "userDetails.poa.title",
        defaultMessage: "Fullmakt",
      })}
    >
      <Stack gap="md">
        <Text size="sm" lh={1.6} c="var(--wallet-color-neutrals-gray-600)">
          <FormattedMessage
            id="userDetails.poa.paragraph"
            defaultMessage="Fullmakten hanteras av Fullmaktskollen och ger oss rätt att ta del av viss information om dina försäkringar och pensioner. Informationen ligger till grund för din personliga rådgivning. Du kan enkelt ändra eller återkalla fullmakten via Fullmaktskollens hemsida."
          />
        </Text>

        <Fact
          header={
            <FormattedMessage
              id="userDetails.poa.duration.title"
              defaultMessage="Fullmaktens giltighetstid"
            />
          }
          value={
            validTo ? (
              dayjs(validTo).format("YYYY-MM-DD")
            ) : (
              <FormattedMessage
                id="app.poaOverview.poaSheet.validityOpenEnded"
                defaultMessage="Tillsvidare"
              />
            )
          }
        />

        <Fact
          header={
            <FormattedMessage
              id="userDetails.poa.reciever.title"
              defaultMessage="Fullmaktshavare"
            />
          }
          value="556974-8378, Sambla Group AB"
        />

        {giver && (
          <Fact
            block
            header={
              <FormattedMessage
                id="userDetails.poa.grantor.title"
                defaultMessage="Fullmaktsgivare"
              />
            }
            value={giver}
          />
        )}

        <Text size="sm" lh={1.6} c="var(--wallet-color-neutrals-gray-600)">
          <FormattedMessage
            id="app.poaOverview.userDetails.contactInfo"
            defaultMessage="Kontakta kundtjänst på 010 - 424 89 24 om du har frågor om fullmakten eller dina pensionsuppgifter."
          />
        </Text>

        <CloseButton onClose={onClose} />
      </Stack>
    </BottomSheet>
  );
}

export function SamblaHandoffSheet({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const intl = useIntl();

  return (
    <BottomSheet
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: "app.poaOverview.moreDetails.title",
        defaultMessage: "Se fler detaljer",
      })}
    >
      <Stack gap="md">
        <Text size="sm" lh={1.6}>
          <FormattedMessage
            id="app.poaOverview.moreDetails.drawer.description"
            defaultMessage="Du skickas vidare till Sambla Pension för att logga in och se fler detaljer på ditt pensionskonto."
          />
        </Text>

        <Button
          fullWidth
          component="a"
          href={`${window.location.origin}/login`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage
            id="app.poaOverview.moreDetails.CTA"
            defaultMessage="Fortsätt till Sambla Pension"
          />
          <VisuallyHidden>
            {" "}
            <FormattedMessage
              id="app.poaOverview.handoff.newTab"
              defaultMessage="(öppnas i ny flik)"
            />
          </VisuallyHidden>
        </Button>
        <CloseButton onClose={onClose} />
      </Stack>
    </BottomSheet>
  );
}
